import React, { useState } from "react";
import { Tooltip } from "@fluentui/react-components";
import { ArrowDown, ArrowUp, ChevronDown, Info, Minus } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { formatDateRange, pluralize } from "./cardFormat";
import { INDEX_EXPLANATION, STATUS_STYLE, formatDelta, formatKpiValue, formatScore } from "./performanceFormat";
import PerformanceGauge from "./PerformanceGauge";
import styles from "./resourceInsights.module.scss";

/** The change since the previous period: an arrow and the points gained or lost, with the period compared with in a tooltip. */
function Trend({ delta, previousStart, previousEnd }) {
  const text = formatDelta(delta);
  if (text === null) return null;

  const Icon = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : Minus;
  const tone = delta > 0 ? styles.trendUp : delta < 0 ? styles.trendDown : styles.trendFlat;

  return (
    <Tooltip content={`Compared with ${formatDateRange(previousStart, previousEnd)}`} relationship="description" positioning="above">
      <span className={`${styles.trend} ${tone}`}>
        <Icon size={12} />
        {text} pts
      </span>
    </Tooltip>
  );
}

/** What the global index rests on, so it can be judged: the tasks and timesheets behind it. */
function basisText(basedOn) {
  const parts = [];
  if (basedOn.dueTasks > 0) parts.push(`${pluralize(basedOn.dueTasks, "task")} due`);
  if (basedOn.activeTasks > 0) parts.push(`${pluralize(basedOn.activeTasks, "task")} under way`);
  if (basedOn.timesheetWeeks > 0) parts.push(pluralize(basedOn.timesheetWeeks, "timesheet week"));
  return parts.length > 0 ? `Based on ${parts.join(", ")}` : "";
}

/** The tasks that explain an index, when it has some to open: the overdue ones for delivery, those under way for schedule. */
const TASKS_BEHIND = {
  delivery: { count: "overdue", label: "See the overdue tasks" },
  schedule: { count: "active", label: "See the tasks under way" },
};

/** One index: its score, the change since the previous period and, once opened, the figures it is made of. */
function IndexTile({ index, previousStart, previousEnd, open, onToggle, showHours, canOpenTasks, onOpenTasks }) {
  const look = STATUS_STYLE[index.status];
  const hasScore = index.score !== null && index.score !== undefined;
  const tasksBehind = canOpenTasks && hasScore ? TASKS_BEHIND[index.key] : null;

  return (
    <div className={`${styles.indexTile} ${open ? styles.indexTileOpen : ""}`} style={{ "--tone": look.color, "--tone-soft": look.soft }}>
      <button type="button" className={styles.indexTileMain} aria-expanded={open} onClick={onToggle}>
        <span className={styles.indexTileTop}>
          <span className={styles.indexName}>{index.name}</span>
          <ChevronDown size={14} className={`${styles.indexChevron} ${open ? styles.indexChevronOpen : ""}`} />
        </span>

        <span className={styles.indexScoreRow}>
          <strong>{formatScore(index.score)}</strong>
          <Trend delta={index.delta} previousStart={previousStart} previousEnd={previousEnd} />
        </span>

        <span className={styles.indexBar}>
          <span style={{ width: `${hasScore ? index.score : 0}%` }} />
        </span>

        <span className={styles.indexStatus}>
          {look.label}
          {index.lowData && " · low data"}
        </span>
      </button>

      {open && (
        <div className={styles.indexDetail}>
          <p className={styles.indexExplanation}>{INDEX_EXPLANATION[index.key]}</p>

          {!hasScore && <p className={styles.note}>{index.note}</p>}

          {index.kpis
            .filter((kpi) => showHours || !kpi.isHours)
            .map((kpi) => (
              <Tooltip key={kpi.key} content={kpi.hint} relationship="description" positioning="above-start">
                <div className={styles.kpiRow}>
                  <span>{kpi.label}</span>
                  <strong>{formatKpiValue(kpi)}</strong>
                </div>
              </Tooltip>
            ))}

          {tasksBehind && (
            <button type="button" className={styles.drillButton} onClick={() => onOpenTasks(tasksBehind.count)}>
              {tasksBehind.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ hint }) {
  return (
    <div className={styles.sectionHeader}>
      <h4>Performance</h4>
      {hint && <span className={styles.sectionHint}>{hint}</span>}
    </div>
  );
}

/**
 * The performance of the resource over the period of Resource Insights: the global index as a gauge, with its status, its
 * change since the previous period and what it rests on, and one tile for each index it is made of. Pressing a tile
 * shows the figures behind it.
 *
 * @param state  `{ status: "loading" | "ready" | "error", performance, reload }`, as `useResourcePerformance` returns it
 * @param showHours  whether the figures made of hours are shown; the scores are the same either way
 * @param onOpenTasks  `(count)`, asks for the list of the tasks behind a count
 */
export default function PerformanceSection({ state, showHours, canOpenTasks, onOpenTasks }) {
  const [openKey, setOpenKey] = useState(null);
  const { status, performance, reload } = state;

  if (status === "error") {
    return (
      <section className={styles.section}>
        <SectionHeader />
        <div className={styles.errorBox}>
          <span>The performance indices could not be loaded.</span>
          <button type="button" className={styles.linkButton} onClick={reload}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  if (!performance) {
    return (
      <section className={styles.section}>
        <SectionHeader />
        <Skeleton height={124} borderRadius={12} />
      </section>
    );
  }

  const { global, indices, basedOn } = performance;
  const look = STATUS_STYLE[global.status];
  const hasScore = global.score !== null && global.score !== undefined;
  const isRefreshing = status === "loading";

  return (
    <section className={`${styles.section} ${isRefreshing ? styles.refreshing : ""}`}>
      <SectionHeader hint={formatDateRange(performance.startDate, performance.endDate)} />

      <div className={styles.headline} style={{ "--tone": look.color, "--tone-soft": look.soft }}>
        <PerformanceGauge score={global.score} color={look.color} />

        <div className={styles.headlineText}>
          <div className={styles.headlineTitle}>
            <strong>{global.name}</strong>
            <Tooltip content={INDEX_EXPLANATION.global} relationship="description" positioning="above">
              <span className={styles.infoIcon}>
                <Info size={13} />
              </span>
            </Tooltip>
          </div>

          <div className={styles.headlineStatus}>
            <span className={styles.statusPill}>{look.label}</span>
            <Trend delta={global.delta} previousStart={performance.previousStartDate} previousEnd={performance.previousEndDate} />
          </div>

          <p className={styles.note}>{hasScore ? basisText(basedOn) : global.note}</p>
          {global.lowData && <p className={styles.lowData}>Based on very little data: read it with care.</p>}
        </div>
      </div>

      <div className={styles.indexGrid}>
        {indices.map((index) => (
          <IndexTile
            key={index.key}
            index={index}
            previousStart={performance.previousStartDate}
            previousEnd={performance.previousEndDate}
            open={openKey === index.key}
            onToggle={() => setOpenKey(openKey === index.key ? null : index.key)}
            showHours={showHours}
            canOpenTasks={canOpenTasks}
            onOpenTasks={onOpenTasks}
          />
        ))}
      </div>
    </section>
  );
}
